import { useState, useEffect, useRef } from "react";
import { Icon } from "components";
import {
    textures,
    mosaicTiles,
    solidTextures,
    normalMaps,
    hardwoods,
    wallTextures,
} from "textures";
import { colors, floorColors, range, initialSettings } from "settings";
import s from "./Panel.module.css";

const Panel = ({ submit, typeMesh }) => {
    const [settings, setSettings] = useState(initialSettings);
    const [isExpanded, setIsExpanded] = useState(false);
    const [needUpdate, setNeedUpdate] = useState(false);
    const [isFirst, setIsFirst] = useState(true);
    const [usersTextures, setUsersTextures] = useState([]);

    const filePicker = useRef(null);

    useEffect(() => {
        const { texture, options, isPlastic } = settings;
        if (needUpdate && (texture || options === "paint" || isPlastic)) {
            submit(settings);
            if (isFirst) {
                setIsFirst(false);
            } else {
                setIsFirst(true);
                setNeedUpdate(false);
                setSettings((prev) => ({ ...prev, isPlastic: false }));
            }
            // setNeedUpdate(false);
            // setSettings((prev) => ({ ...prev, isPlastic: false }));
        }
    }, [settings, submit, needUpdate, isFirst]);
    // }, [settings, submit, needUpdate]);

    const chooseFile = (file) => {
        const image = new Image(1024, 1024);
        const url = window.URL.createObjectURL(file);
        image.src = url;
        setUsersTextures((prev) => [...prev, url]);
    };

    const handlePick = () => {
        filePicker.current.click();
    };

    const onChange = (e) => {
        const { value, name } = e.target;
        const isTile = name.startsWith("carpet") ? false : true;
        setSettings((prev) => ({ ...prev, [name]: value, isTile }));
    };

    const valueCorrection = (e) => {
        const { name } = e.target;
        const { min, max } = range[name];
        const value = settings[name];
        setSettings((prev) => ({
            ...prev,
            [name]: Math.min(max, Math.max(value, min)),
        }));
        setNeedUpdate(true);
    };

    const setTexture = (texture, i = -1) => {
        const { tileHeight, tileWidth } = settings;

        if (i < 0) {
            setSettings((prev) => ({
                ...prev,
                tileHeight: tileHeight > tileWidth ? prev.tileWidth : prev.tileHeight,
                tileWidth: tileHeight > tileWidth ? prev.tileHeight : prev.tileWidth,
                texture,
                isTile: true,
                normalMap: null,
            }));
        } else {
            setSettings((prev) => ({
                ...prev,
                tileHeight: prev.tileWidth,
                seamSize: prev.tileWidth / 750,
                seamColor: i === 0 ? "#101010" : "#888888",
                texture,
                isTile: true,
                normalMap: normalMaps[i],
            }));
        }
        setNeedUpdate(true);
    };

    const setProp = (name, value) => {
        setSettings((prev) => ({ ...prev, [name]: value, isTile: true }));
        if (name !== "options") setNeedUpdate(true);
    };

    const setCarpet = (texture, length) => {
        setSettings((prev) => ({ ...prev, texture, isTile: false, length }));
        setNeedUpdate(true);
    };

    const setHardwood = (name, value) => {
        setSettings((prev) => ({ ...prev, [name]: value, isTile: false }));
        if (settings.length === 1) setNeedUpdate(true);
    };

    const setInches = (inches) => {
        setSettings((prev) => ({ ...prev, inches, isTile: false }));
        if (settings.length === 1) setNeedUpdate(true);
    };

    const setRotate = () => {
        const floorRotate = (settings.floorRotate + 1) % 3;
        setSettings((prev) => ({ ...prev, floorRotate, isTile: false }));
        if (settings.length === 1) setNeedUpdate(true);
    };

    const toggleExpand = () => {
        setIsExpanded((state) => !state);
    };

    return (
        <div className={s.container}>
            <div className={s.prop}>
                <div className={s.flexBox}>
                    <div className={s.btnBox}>
                        <button
                            className={settings.options === "tile" ? s.btn : s.passiveBtn}
                            type="button"
                            onClick={() => setProp("options", "tile")}
                        >
                            Tile
                        </button>
                        {typeMesh !== "showerFlor" && (
                            <>
                                <button
                                    className={settings.options === "paint" ? s.btn : s.passiveBtn}
                                    type="button"
                                    onClick={() => setProp("options", "paint")}
                                >
                                    Paint
                                </button>

                                <button
                                    className={settings.options === "paper" ? s.btn : s.passiveBtn}
                                    type="button"
                                    onClick={() => setProp("options", "paper")}
                                >
                                    Wallpaper
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {settings.options === "tile" && (
                <>
                    <div className={s.prop}>
                        <div className={s.flexBetween} onClick={toggleExpand}>
                            <div className={s.flexBox}>
                                <p>Grout color: </p>
                                <div
                                    className={s.choosenColor}
                                    style={{
                                        backgroundColor: settings.seamColor,
                                    }}
                                ></div>
                            </div>
                            <Icon icon={isExpanded ? "collaps" : "expand"} w={20} />
                        </div>
                        <ul
                            className={s.colorList}
                            style={{ height: isExpanded ? "600px" : "60px" }}
                        >
                            {colors.map((color, i) => (
                                <li
                                    className={color === settings.seamColor ? s.active : s.item}
                                    key={color + i}
                                    onClick={() => setProp("seamColor", color)}
                                >
                                    <div
                                        className={s.color}
                                        style={{ backgroundColor: color }}
                                    ></div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className={s.flexBox + " " + s.prop}>
                        <p>Seam size (cm)</p>
                        <input
                            name="seamSize"
                            className={s.input}
                            value={settings.seamSize}
                            onChange={onChange}
                            onBlur={valueCorrection}
                            type="number"
                        />
                    </div>
                    <div className={s.flexBox + " " + s.prop}>
                        <p>Tile size (cm)</p>
                        <input
                            name="tileWidth"
                            className={s.input}
                            value={settings.tileWidth}
                            onChange={onChange}
                            onBlur={valueCorrection}
                            type="number"
                        />
                        <p>X</p>
                        <input
                            name="tileHeight"
                            className={s.input}
                            value={settings.tileHeight}
                            onChange={onChange}
                            onBlur={valueCorrection}
                            type="number"
                        />
                    </div>
                    <div className={s.prop}>
                        <p>Position: </p>
                        <div className={s.flexBox}>
                            <div className={s.btnBox} style={{ width: "218px" }}>
                                <button
                                    className={settings.pos === "horizontal" ? s.btn : s.passiveBtn}
                                    style={{ width: "106px" }}
                                    type="button"
                                    onClick={() => setProp("pos", "horizontal")}
                                >
                                    Horizontal
                                </button>
                                <button
                                    className={settings.pos === "vertical" ? s.btn : s.passiveBtn}
                                    style={{ width: "106px" }}
                                    type="button"
                                    onClick={() => setProp("pos", "vertical")}
                                >
                                    Vertical
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => setProp("turned", !settings.turned)}
                            >
                                <Icon icon="rotate" w={20} />
                            </button>
                        </div>
                    </div>
                    <div className={s.prop}>
                        <p>Matterial: </p>
                        <div className={s.flexBox}>
                            <div className={s.btnBox} style={{ width: "218px" }}>
                                <button
                                    className={
                                        settings.material === "polish" ? s.btn : s.passiveBtn
                                    }
                                    type="button"
                                    style={{ width: "106px" }}
                                    onClick={() => setProp("material", "polish")}
                                >
                                    Polish
                                </button>
                                <button
                                    className={settings.material === "matt" ? s.btn : s.passiveBtn}
                                    type="button"
                                    style={{ width: "106px" }}
                                    onClick={() => setProp("material", "matt")}
                                >
                                    Matt
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className={s.prop}>
                        <p>Tile texture</p>
                        <ul className={s.colorList}>
                            {textures.map((image, i) => (
                                <li
                                    className={
                                        image === settings.texture ? s.activeTexture : s.texture
                                    }
                                    key={image}
                                    onClick={() => setTexture(image)}
                                >
                                    <img src={image} width={60} alt="" />
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* <div className={s.flexBox + " " + s.prop}>
                        <p>Tiling: </p>
                        <label className={s.flexBox}>
                            Whole
                            <input
                                type="radio"
                                name="tiling"
                                defaultChecked
                                onChange={(e) => setTiling(e.target.value)}
                                value="whole"
                            />
                        </label>

                        <label className={s.flexBox}>
                            Top
                            <input
                                type="radio"
                                name="tiling"
                                onChange={(e) => setTiling(e.target.value)}
                                value="top"
                            />
                        </label>
                        <label className={s.flexBox}>
                            Bottom
                            <input
                                type="radio"
                                name="tiling"
                                onChange={(e) => setTiling(e.target.value)}
                                value="bottom"
                            />
                        </label>
                    </div> */}

                    {typeMesh !== "showerFlor" && (
                        <div className={s.prop}>
                            <p>Mosaic tiles textures</p>
                            <ul className={s.colorList}>
                                {mosaicTiles.map((image, i) => (
                                    <li
                                        className={
                                            image === settings.texture ? s.activeTexture : s.texture
                                        }
                                        key={image}
                                        onClick={() => setTexture(image, i)}
                                    >
                                        <img src={image} width={60} alt="" />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {typeMesh === "showerFloor" && (
                        <div className={s.prop}>
                            <p>Set material as white plastic </p>
                            <button
                                className={s.btn}
                                type="button"
                                onClick={() => setProp("isPlastic", true)}
                            >
                                Aplly
                            </button>
                        </div>
                    )}

                    {/* <div className={s.prop + " none"}>
                        <button className={s.btn} onClick={addEdge}>
                            Add tile edge
                        </button>
                    </div> */}

                    {typeMesh === "floor" && (
                        <>
                            <div className={s.prop}>
                                <p>Carpet textures</p>
                                <ul className={s.colorList}>
                                    {solidTextures.map((image, i) => (
                                        <li
                                            className={
                                                image === settings.texture
                                                    ? s.activeTexture
                                                    : s.texture
                                            }
                                            key={image}
                                            onClick={() => setCarpet(image, 2)}
                                        >
                                            <img src={image} width={60} alt="" />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className={s.prop}>
                                <p>Hardwood Floor</p>

                                <p style={{ fontWeight: 400 }}>Colors:</p>
                                <ul className={s.colorList}>
                                    {floorColors.map((color) => (
                                        <li
                                            className={
                                                color === settings.floorColor ? s.active : s.item
                                            }
                                            key={color}
                                            onClick={() => setHardwood("floorColor", color)}
                                        >
                                            <div
                                                className={s.color}
                                                style={{
                                                    backgroundColor: color,
                                                }}
                                            ></div>
                                        </li>
                                    ))}
                                </ul>

                                <div className={s.flexBox}>
                                    <ul
                                        className={s.colorList}
                                        style={{
                                            width: "80px",
                                        }}
                                    >
                                        {hardwoods.map((image, i) => (
                                            <li
                                                className={
                                                    image === settings.texture
                                                        ? s.activeTexture
                                                        : s.texture
                                                }
                                                key={image}
                                                onClick={() => setCarpet(image, 1)}
                                            >
                                                <img src={image} width={60} alt="" />
                                            </li>
                                        ))}
                                    </ul>
                                    <div>
                                        <p style={{ fontWeight: 400 }}>Width (inches)</p>
                                        <div
                                            className={s.btnBox}
                                            style={{
                                                width: "76px",
                                            }}
                                        >
                                            <button
                                                className={
                                                    settings.inches === 6 ? s.btn : s.passiveBtn
                                                }
                                                type="button"
                                                style={{
                                                    width: "36px",
                                                    height: "30px",
                                                }}
                                                onClick={() => setInches(6)}
                                            >
                                                6
                                            </button>
                                            <button
                                                className={
                                                    settings.inches === 2.5 ? s.btn : s.passiveBtn
                                                }
                                                type="button"
                                                style={{
                                                    width: "36px",
                                                    height: "30px",
                                                }}
                                                onClick={() => setInches(2.5)}
                                            >
                                                2.5
                                            </button>
                                        </div>
                                    </div>
                                    <button type="button" onClick={setRotate}>
                                        <Icon icon="rotate" w={20} />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            {settings.options === "paint" && (
                <div className={s.prop}>
                    <div className={s.flexBetween} onClick={toggleExpand}>
                        <div className={s.flexBox}>
                            <p>Paint color: </p>
                            <div
                                className={s.choosenColor}
                                style={{
                                    backgroundColor: settings.seamColor,
                                }}
                            ></div>
                        </div>
                        <Icon icon={isExpanded ? "collaps" : "expand"} w={20} />
                    </div>
                    <ul className={s.colorList} style={{ height: isExpanded ? "600px" : "60px" }}>
                        {colors.map((color, i) => (
                            <li
                                className={color === settings.seamColor ? s.active : s.item}
                                key={color + i}
                                onClick={() => setProp("seamColor", color)}
                            >
                                <div className={s.color} style={{ backgroundColor: color }}></div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {settings.options === "paper" && (
                <>
                    <div className={s.prop}>
                        <p>User's Textures</p>
                        <ul className={s.colorList}>
                            <div className={s.texture} onClick={handlePick}>
                                <Icon icon="upload" w={34} />
                                <p className={s.text}>upload</p>
                                <input
                                    className="hidden"
                                    type="file"
                                    ref={filePicker}
                                    accept=".jpg, .jpeg, .png, .webp"
                                    onChange={(e) => chooseFile(e.target.files[0])}
                                />
                            </div>
                            {usersTextures.map((image) => (
                                <li
                                    className={
                                        image === settings.texture ? s.activeTexture : s.texture
                                    }
                                    key={image}
                                    onClick={() => setProp("texture", image)}
                                >
                                    <img src={image} width={60} alt="" />
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={s.prop}>
                        <p>Wallpaper textures:</p>
                        <ul className={s.colorList}>
                            {wallTextures.map((image) => (
                                <li
                                    className={
                                        image === settings.texture ? s.activeTexture : s.texture
                                    }
                                    key={image}
                                    onClick={() => setProp("texture", image)}
                                >
                                    <img src={image} width={60} alt="" />
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={s.flexBox + " " + s.prop}>
                        <p>Roll width (cm)</p>
                        <input
                            name="rollWidth"
                            className={s.input}
                            value={settings.rollWidth}
                            onChange={onChange}
                            onBlur={valueCorrection}
                            type="number"
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default Panel;
